import React, { useEffect, useState } from "react";
import { Alert, Avatar, Button, Collapse, Descriptions, Empty, Modal, Space, Spin, Table, Tabs, Tag, Tooltip, Typography, theme } from "antd";
import { BarChartOutlined, CoffeeOutlined, FullscreenExitOutlined, FullscreenOutlined, InfoCircleOutlined, TagOutlined } from "@ant-design/icons";
import moment from "moment";
import useIsMobile from "../hooks/useIsMobile";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import RowActionsMenu from "../components/RowActionsMenu";
import { ProductionHistoryStatus, productionResourceNotice, useProductionHistoryStyle } from "./ProductionHistoryWorkspace";
import "./production-details-modal.css";

const { Title, Text } = Typography;
const quantity = value => value == null || value === "" || !Number.isFinite(Number(value)) ? "—"
  : Number(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 6 });

export default function ProductionDetailsModal({
  open, onClose, resource, productionTerminology, formatCurrency,
  canDeclareLotLoss, canRegularizeVariableCost, canPayVariableCostExpense,
  handleOpenLotLossModal, handleOpenVariableCostRegularization, handleOpenExpensePayment,
  getLotStatusColor, getLotStatusLabel, getLossTypeLabel,
}) {
  const isMobile = useIsMobile();
  const { token } = theme.useToken();
  const style = { ...useProductionHistoryStyle(), "--pd-error": token.colorErrorText };
  const [activeTab, setActiveTab] = useState("materials");
  const [expanded, setExpanded] = useState(false);
  const [recipeExpanded, setRecipeExpanded] = useState(false);
  useEffect(() => { if (!open) { setActiveTab("materials"); setExpanded(false); setRecipeExpanded(false); } }, [open]);
  const notice = productionResourceNotice(resource);
  const selectedProductionDetails = notice.data;
  const production = selectedProductionDetails?.production || {};
  const summary = selectedProductionDetails?.summary || {};
  const fresh = resource.status === "verified" && !notice.error;
  const money = value => value == null || value === "" ? "—" : formatCurrency(value);
  const date = production.created_at ? moment(production.created_at) : null;
  const pending = Number(selectedProductionDetails?.variable_cost_financial_summary?.pending_regularization_count || 0);
  const recipeStatus = selectedProductionDetails?.recipe_snapshot?.guardrail_status;
  const cuisine = productionTerminology.costContext === "cuisine";
  const finance = [
    [cuisine ? "Coût des ingrédients" : "Coût des matières", summary.total_material_cost],
    ["Coûts additionnels", summary.total_variable_cost],
    ["Coût total réel", summary.total_cost_real, "total"],
    ["Coût unitaire", summary.unit_production_cost],
    ["Valeur des produits", summary.total_product_value],
    ["Valeur des pertes", summary.post_production_loss_cost],
    ["Marge brute", summary.profit_margin, Number(summary.profit_margin) < 0 ? "negative" : undefined],
  ];
  const facts = [
    ["Code lot", production.production_code || "—"],
    ["Préparé par", production.user_name || "Système"],
    ["Établissement", production.warehouse_name || "—"],
    ["Entreprise", production.company_name || "—"],
  ];
  const materials = selectedProductionDetails ? (<>
{/* Section pour les matières premières */}
<div className="production-details__section">
  <Title
    level={5}
    style={{ display: "flex", alignItems: "center", gap: 8 }}
  >
    <TagOutlined />
    {productionTerminology.consumedMaterialsTitle}
  </Title>
  {selectedProductionDetails.materials &&
  selectedProductionDetails.materials.length > 0 ? (
    <Table
      dataSource={selectedProductionDetails.materials}
      pagination={false}
      size="small"
      rowKey="product_id"
      columns={[
        {
          title: "Produit",
          dataIndex: "product_name",
          key: "product_name",
          render: (text, record) => (
            <Space>
              {record.image && (
                <Avatar
                  src={record.image}
                  size="small"
                />
              )}
              <span>{text}</span>
            </Space>
          ),
        },
        {
          title: "Quantité utilisée",
          dataIndex: "used_quantity",
          key: "used_quantity",
          align: "right",
          render: (text, record) => (
            <span>
              {text} {record.unit_short_name || record.unit_name}
            </span>
          ),
        },
        {
          title: "Prix unitaire",
          dataIndex: "purchase_price",
          key: "purchase_price",
          align: "right",
          render: (text) => formatCurrency(text),
        },
        {
          title: "Coût total",
          dataIndex: "total_cost",
          key: "total_cost",
          align: "right",
          render: (text) => formatCurrency(text),
        },
      ]}
      summary={(pageData) => (
        <Table.Summary.Row className="production-details__total-row">
          <Table.Summary.Cell index={0} colSpan={3} align="right">
              <Text strong>
                {productionTerminology.totalMaterialsLabel} :
              </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <Text strong>
              {formatCurrency(
                selectedProductionDetails.summary
                  .total_material_cost
              )}
            </Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  ) : (
    <Alert
      message={productionTerminology.noMaterialFound}
      description={productionTerminology.noMaterialAssociatedSentence}
      type="info"
      showIcon
      style={{ marginTop: 8 }}
    />
  )}
</div>
  </>) : null;
  const costs = selectedProductionDetails ? (<>
{/* Section pour les coûts variables */}
<div className="production-details__section">
  <Title
    level={5}
    style={{ display: "flex", alignItems: "center", gap: 8 }}
  >
    <TagOutlined />
    Coûts variables additionnels
  </Title>
  {selectedProductionDetails.variable_costs &&
  selectedProductionDetails.variable_costs.length > 0 ? (
    <>
      {Number(
        selectedProductionDetails
          .variable_cost_financial_summary
          ?.pending_regularization_count || 0
      ) > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${selectedProductionDetails.variable_cost_financial_summary.pending_regularization_count} charge(s) à régulariser`}
          description="Le coût est bien inclus dans le coût de revient, mais aucune dépense financière n'a encore été créée."
        />
      )}
      <Table
      dataSource={selectedProductionDetails.variable_costs}
      pagination={false}
      size="small"
      scroll={{ x: "max-content" }}
      rowKey={(record) =>
        record.id
          ? record.id.toString()
          : `${record.label}-${record.cost_per_unit}`
      }
      columns={[
        {
          title: "Libellé",
          dataIndex: "label",
          key: "label",
        },
        {
          title: "Fournisseur",
          dataIndex: "supplier_name",
          key: "supplier_name",
          render: (value) =>
            value || <Text type="secondary">À compléter</Text>,
        },
        {
          title: "Catégorie de dépense",
          dataIndex: "expense_category_name",
          key: "expense_category_name",
          render: (value) =>
            value || <Text type="secondary">À compléter</Text>,
        },
        {
          title: "Coût unitaire",
          dataIndex: "cost_per_unit",
          key: "cost_per_unit",
          align: "right",
          render: (text) => formatCurrency(text),
        },
        {
          title: "Qté produite",
          dataIndex: "output_quantity",
          key: "output_quantity",
          align: "right",
          render: (value) => Number(value || 0).toFixed(2),
        },
        {
          title: "Coût total",
          dataIndex: "total_cost",
          key: "total_cost",
          align: "right",
          render: (text) => formatCurrency(text),
        },
        {
          title: "Suivi financier",
          key: "financial_status",
          render: (_, record) => {
            if (record.financial_status === "cancelled") {
              return <Tag>Annulée</Tag>;
            }
            if (record.financial_status === "not_applicable") {
              return <Tag>Sans montant</Tag>;
            }
            if (record.financial_status !== "expense_created") {
              return <Tag color="orange">À régulariser</Tag>;
            }
            const color =
              record.payment_status === "Payé"
                ? "green"
                : record.payment_status === "Partiellement payé"
                ? "gold"
                : "blue";
            return (
              <Space direction="vertical" size={2}>
                <Tag color={color}>
                  {record.payment_status || "Non payé"}
                </Tag>
                {record.due_amount !== null && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Reste : {formatCurrency(record.due_amount)}
                  </Text>
                )}
              </Space>
            );
          },
        },
        {
          title: "Action",
          key: "financial_action",
          fixed: "right",
          width: 58,
          align: "center",
          render: (_, record) => <RowActionsMenu ariaLabel={`Actions de la charge ${record.label || ""}`} items={[
            record.can_regularize && canRegularizeVariableCost ? { key: "regularize", label: "Régulariser", "aria-label": "Régulariser", disabled: !fresh,
              onClick: () => handleOpenVariableCostRegularization(record) } : null,
            record.expense_id && Number(record.due_amount || 0) > 0 && canPayVariableCostExpense ? { key: "pay", label: "Payer", "aria-label": "Payer", disabled: !fresh,
              onClick: () => handleOpenExpensePayment(record.expense_id) } : null,
          ]} />,
        },
      ]}
      summary={() => (
        <Table.Summary.Row className="production-details__total-row">
          <Table.Summary.Cell index={0} colSpan={5} align="right">
            <Text strong>Total coûts variables :</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <Text strong>
              {formatCurrency(
                selectedProductionDetails.summary
                  .total_variable_cost
              )}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} colSpan={2} />
        </Table.Summary.Row>
      )}
      />
    </>
  ) : (
    <Alert
      message="Aucun coût variable"
      description="Aucun coût variable n'est associé à cette production."
      type="info"
      showIcon
      style={{ marginTop: 8 }}
    />
  )}
</div>
  </>) : null;
  const outputs = selectedProductionDetails ? (<>
{/* Section pour les produits finis */}
<div className="production-details__section">
  <Title
    level={5}
    style={{ display: "flex", alignItems: "center", gap: 8 }}
  >
    <TagOutlined />
    Produits finis obtenus
  </Title>
  {selectedProductionDetails.outputs &&
  selectedProductionDetails.outputs.length > 0 ? (
    <Table
      dataSource={selectedProductionDetails.outputs}
      pagination={false}
      size="small"
      rowKey="product_id"
      columns={[
        {
          title: "Produit",
          dataIndex: "product_name",
          key: "product_name",
          render: (text, record) => (
            <Space>
              {record.image && (
                <Avatar
                  src={record.image}
                  size="small"
                />
              )}
              <span>{text}</span>
            </Space>
          ),
        },
        {
          title: "Quantité produite",
          dataIndex: "produced_quantity",
          key: "produced_quantity",
          align: "right",
          render: (text, record) => (
            <span>
              {text} {record.unit_short_name || record.unit_name}
            </span>
          ),
        },
        {
          title: "Prix de vente unitaire",
          dataIndex: "sales_price",
          key: "sales_price",
          align: "right",
          render: (text) => formatCurrency(text),
        },
        {
          title: "Valeur totale",
          dataIndex: "total_value",
          key: "total_value",
          align: "right",
          render: (text) => formatCurrency(text),
        },
      ]}
      summary={(pageData) => (
        <Table.Summary.Row className="production-details__total-row">
          <Table.Summary.Cell index={0} colSpan={3} align="right">
            <Text strong>Total produits finis :</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <Text strong>
              {formatCurrency(
                selectedProductionDetails.summary
                  .total_product_value
              )}
            </Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  ) : (
    <Alert
      message="Aucun produit fini"
      description="Aucun produit fini n'est associé à cette production."
      type="info"
      showIcon
      style={{ marginTop: 8 }}
    />
  )}
</div>
  </>) : null;
  const lots = selectedProductionDetails ? (<>
{/* Résumé financier */}
<div className="production-details__section">
  <Title
    level={5}
    style={{ display: "flex", alignItems: "center", gap: 8 }}
  >
    <TagOutlined />
    Lots de production
  </Title>
  {Boolean(
    selectedProductionDetails?.production?.is_regularization
  ) && (
    <Alert
      message="Production de régularisation"
      description={`Cette production a été générée pour solder le stock négatif de ventes déjà réalisées (${productionTerminology.configurationSingular} « vendable sans production »). Sa quantité est donc déjà écoulée : aucune perte ne peut être déclarée sur ses lots.`}
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
    />
  )}
  {selectedProductionDetails.summary?.lot_tracking_enabled &&
    !selectedProductionDetails.summary?.lot_loss_tracking_enabled && (
      <Alert
        message="Suivi des pertes indisponible"
        description="La declaration de pertes sur lot necessite l'application de la migration de base de donnees correspondante."
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
      />
    )}
  {selectedProductionDetails.output_lots &&
  selectedProductionDetails.output_lots.length > 0 ? (
    <Table
      dataSource={selectedProductionDetails.output_lots}
      pagination={false}
      size="small"
      rowKey="lot_id"
      columns={[
        {
          title: "Lot",
          dataIndex: "lot_code",
          key: "lot_code",
          render: (value) => <Tag color="purple">{value}</Tag>,
        },
        {
          title: "Produit",
          dataIndex: "product_name",
          key: "product_name",
        },
        {
          title: "Qte produite",
          dataIndex: "quantity_produced",
          key: "quantity_produced",
          align: "right",
          render: (value, record) => (
            <span>
              {value} {record.unit_short_name || record.unit_name}
            </span>
          ),
        },
        {
          title: "Qte disponible",
          dataIndex: "quantity_available",
          key: "quantity_available",
          align: "right",
          render: (value, record) => (
            <span>
              {value} {record.unit_short_name || record.unit_name}
            </span>
          ),
        },
        {
          title: "Pertes cumulees",
          dataIndex: "loss_quantity_total",
          key: "loss_quantity_total",
          align: "right",
          render: (value, record) => (
            <span>
              {value} {record.unit_short_name || record.unit_name}
            </span>
          ),
        },
        {
          title: "Statut",
          dataIndex: "status",
          key: "status",
          align: "center",
          render: (value) => (
            <Tag color={getLotStatusColor(value)}>
              {getLotStatusLabel(value)}
            </Tag>
          ),
        },
        {
          title: "Actions",
          key: "actions",
          align: "center",
          render: (_, lot) => {
            const availableQuantity = Number(
              lot.quantity_available || 0
            );
            // Production de regularisation : la sortie a deja ete
            // vendue (elle solde le stock negatif), aucun stock
            // physique a perdre => pas de declaration de perte.
            const isRegularizationProduction = Boolean(
              selectedProductionDetails?.production
                ?.is_regularization
            );
            const canOpenLossModal =
              selectedProductionDetails.summary
                ?.lot_loss_tracking_enabled &&
              canDeclareLotLoss &&
              !isRegularizationProduction &&
              availableQuantity > 0 &&
              String(lot.status || "").toLowerCase() !== "cancelled";

            if (canOpenLossModal) {
              return <RowActionsMenu ariaLabel={`Actions du lot ${lot.lot_code || lot.lot_id}`} items={[
                { key: "loss", label: "Déclarer une perte", "aria-label": "Déclarer une perte", danger: true, disabled: !fresh, onClick: () => handleOpenLotLossModal(lot) },
              ]} />;
            }

            if (isRegularizationProduction) {
              return (
                <Tooltip title="Production de régularisation : la quantité a déjà été vendue (elle solde le stock négatif). Aucun stock physique à perdre.">
                  <Text type="secondary">
                    Régularisation — perte non applicable
                  </Text>
                </Tooltip>
              );
            }

            return <Text type="secondary">Aucune action</Text>;
          },
        },
      ]}
    />
  ) : (
    <Alert
      message="Aucun lot disponible"
      description="Aucun lot de sortie n'est disponible pour cette production."
      type="info"
      showIcon
      style={{ marginTop: 8 }}
    />
  )}
</div>
  </>) : null;
  const losses = selectedProductionDetails ? (<>
<div className="production-details__section">
  <Title
    level={5}
    style={{ display: "flex", alignItems: "center", gap: 8 }}
  >
    <TagOutlined />
    Pertes post-production
  </Title>
  {selectedProductionDetails.lot_losses &&
  selectedProductionDetails.lot_losses.length > 0 ? (
    <Table
      dataSource={selectedProductionDetails.lot_losses}
      pagination={false}
      size="small"
      rowKey="id"
      columns={[
        {
          title: "Date",
          dataIndex: "created_at",
          key: "created_at",
          render: (value) =>
            value ? moment(value).format("DD/MM/YYYY HH:mm") : "N/A",
        },
        {
          title: "Lot",
          dataIndex: "lot_code_snapshot",
          key: "lot_code_snapshot",
          render: (value) => <Tag color="purple">{value}</Tag>,
        },
        {
          title: "Motif",
          dataIndex: "loss_type",
          key: "loss_type",
          render: (value, record) => (
            <Tag color="volcano">
              {record.loss_type_label || getLossTypeLabel(value)}
            </Tag>
          ),
        },
        {
          title: "Quantite",
          dataIndex: "quantity",
          key: "quantity",
          align: "right",
        },
        {
          title: "Valeur de perte",
          dataIndex: "total_cost_snapshot",
          key: "total_cost_snapshot",
          align: "right",
          render: (value) => formatCurrency(value),
        },
        {
          title: "Utilisateur",
          dataIndex: "user_name",
          key: "user_name",
          render: (value) =>
            value || <Text type="secondary">Systeme</Text>,
        },
        {
          title: "Notes",
          dataIndex: "notes",
          key: "notes",
          render: (value) =>
            value || <Text type="secondary">Aucune</Text>,
        },
      ]}
    />
  ) : (
    <Alert
      message="Aucune perte declaree"
      description="Aucune perte post-production n'a ete declaree sur les lots de cette production."
      type="info"
      showIcon
      style={{ marginTop: 8 }}
    />
  )}
</div>
  </>) : null;
  const recipe = selectedProductionDetails ? (<>
{selectedProductionDetails.recipe_snapshot && (
  <div className="production-details__section">
    <Title
      level={5}
      style={{ display: "flex", alignItems: "center", gap: 8 }}
    >
      <InfoCircleOutlined />
      {productionTerminology.configurationVersionAndControlTitle}
    </Title>
    <Descriptions bordered column={2} size="small">
      <Descriptions.Item
        label={productionTerminology.configurationVersionLabel}
      >
        <Tag color="blue">
          {selectedProductionDetails.recipe_snapshot.recipe_version_label}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item label="Statut garde-fou">
        <Tag
          color={
            selectedProductionDetails.recipe_snapshot.guardrail_status ===
            "blocked"
              ? "red"
              : selectedProductionDetails.recipe_snapshot
                  .guardrail_status === "warning"
              ? "orange"
              : "green"
          }
        >
          {String(
            selectedProductionDetails.recipe_snapshot.guardrail_status ||
              "ok"
          ).toUpperCase()}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item
        label={productionTerminology.configurationRecentlyModifiedLabel}
      >
        {selectedProductionDetails.recipe_snapshot
          .recipe_recently_modified ? (
          <Tag color="volcano">Oui</Tag>
        ) : (
          <Tag color="green">Non</Tag>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Confirmation utilisateur">
        {selectedProductionDetails.recipe_snapshot
          .guardrail_acknowledged ? (
          <Tag color="green">Confirmee</Tag>
        ) : (
          <Tag>Non requise</Tag>
        )}
      </Descriptions.Item>
      {selectedProductionDetails.recipe_snapshot.recipe_updated_at && (
        <Descriptions.Item
          label={productionTerminology.configurationLastUpdatedLabel}
          span={2}
        >
          {moment(
            selectedProductionDetails.recipe_snapshot.recipe_updated_at
          ).format("DD/MM/YYYY HH:mm")}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="Cout standard estime">
        {formatCurrency(
          selectedProductionDetails.recipe_snapshot.standard_total_cost
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Cout FIFO estime">
        {formatCurrency(
          selectedProductionDetails.recipe_snapshot.estimated_total_cost
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Ecart pre-lancement" span={2}>
        {formatCurrency(
          selectedProductionDetails.recipe_snapshot.delta_amount
        )}{" "}
        (
        {Math.round(
          Number(selectedProductionDetails.recipe_snapshot.delta_ratio || 0) *
            100
        )}{" "}
        %)
      </Descriptions.Item>
    </Descriptions>

    {selectedProductionDetails.recipe_snapshot.snapshot_payload
      ?.pricing_guardrails?.warning_reasons?.length > 0 && (
      <Alert
        message="Alertes pre-lancement"
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {selectedProductionDetails.recipe_snapshot.snapshot_payload.pricing_guardrails.warning_reasons.map(
              (item, index) => (
                <li key={`${item?.code || "warning"}-${index}`}>
                  {item?.message || item}
                </li>
              )
            )}
          </ul>
        }
        type="warning"
        showIcon
        style={{ marginTop: 12 }}
      />
    )}
  </div>
)}
  </>) : null;
  const tabs = [
    { key: "materials", label: productionTerminology.materialsTitle, children: materials },
    { key: "costs", label: "Coûts additionnels", children: costs },
    { key: "outputs", label: "Produits obtenus", children: outputs },
    { key: "lots", label: "Lots et pertes", children: <>{lots}{losses}</> },
  ];
  return <Modal open={open} onCancel={onClose}
    className={`production-details-dialog${expanded ? " production-details-dialog--expanded" : ""}`}
    style={{ ...style, top: isMobile || expanded ? 12 : 20 }}
    width={isMobile || expanded ? "calc(100vw - 24px)" : "min(1240px, calc(100vw - 40px))"}
    title={<div className="production-details__heading" role="group" aria-label={productionTerminology.historyDetailsTitle}>
      <div className="production-details__identity"><CoffeeOutlined aria-hidden="true" /><div>
        <h2>{productionTerminology.historyDetailsTitle}</h2>
        {production.production_unit_name && <p>{production.production_unit_name}{date?.isValid() && <> · {date.format("DD/MM/YYYY à HH:mm")}</>}</p>}
      </div></div>
      <div className="production-details__header-actions">
        {selectedProductionDetails && <><ProductionHistoryStatus record={production} /><Tag>{production.production_kind === "semi_finished" ? "Produit semi-fini" : "Produit fini"}</Tag></>}
        {!isMobile && <Tooltip title={expanded ? "Réduire la fenêtre" : "Agrandir la fenêtre"}><Button type="text"
          aria-label={expanded ? "Réduire la fenêtre" : "Agrandir la fenêtre"}
          aria-pressed={expanded}
          icon={expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={() => setExpanded(value => !value)} /></Tooltip>}
      </div>
    </div>}
    footer={<div className="production-details__footer"><span>Consultation des détails de la {productionTerminology.costContext}</span><Button type="primary" aria-label="Fermer" onClick={onClose}>Fermer</Button></div>}
  >
    <BusinessResourceNotice resource={notice} label="la production sélectionnée" />
    {!selectedProductionDetails ? resource.loading ? <div className="production-details__loading"><Spin size="large" /><p>Chargement des détails…</p></div>
      : <Empty description={notice.error ? "Détails indisponibles" : "Aucun détail disponible"} /> : <>
      <dl className="production-details__context">{facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        {production.production_batch_id && <div><dt>Lot groupe</dt><dd>#{production.production_batch_id} · ligne {production.production_batch_sequence}/{production.production_batch_item_count}
          {["partially_cancelled", "cancelled"].includes(production.production_batch_status) && <Tag>{production.production_batch_status === "cancelled" ? "Annulé" : "Partiellement annulé"}</Tag>}
        </dd></div>}
        {production.notes && <div className="production-details__notes"><dt>Notes</dt><dd>{production.notes}</dd></div>}
      </dl>
      {pending > 0 && <Alert className="production-details__priority" type="warning" showIcon message={`${pending} charge(s) à régulariser`}
        action={<Button size="small" aria-label="Voir les charges" onClick={() => setActiveTab("costs")}>Voir les charges</Button>} />}
      {["warning", "blocked"].includes(recipeStatus) && <Alert className="production-details__priority" type="warning" showIcon
        message="Alerte de contrôle enregistrée avant le lancement"
        action={<Button size="small" onClick={() => setRecipeExpanded(true)}>Voir les contrôles</Button>} />}
      <div className="production-details__layout">
        <div className="production-details__main">
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />
          <Collapse className="production-details__recipe" activeKey={recipeExpanded ? ["recipe"] : []} onChange={keys => setRecipeExpanded(keys.length > 0)} items={[{ key: "recipe", label: `${productionTerminology.configurationVersionLabel} et contrôles`,
            children: recipe || <p>Aucune version enregistrée pour cette production.</p> }]} />
        </div>
        <aside className="production-details__summary" aria-label={`Résumé de la ${productionTerminology.costContext}`}>
          <h3><BarChartOutlined aria-hidden="true" /> Résumé de la {productionTerminology.costContext}</h3>
          <div className="production-details__quantities"><div><span>Quantité produite</span><strong>{quantity(summary.output_quantity)}</strong></div>
            <div><span>Disponible sur lots</span><strong>{quantity(summary.available_output_quantity)}</strong></div></div>
          {Number(summary.post_production_loss_quantity) > 0 && <p>Pertes post-production : {quantity(summary.post_production_loss_quantity)}</p>}
          <h3>Coûts et valorisation</h3>
          <dl>{finance.map(([label, value, emphasis]) => <div key={label} role="group" aria-label={label} className={emphasis ? `production-details__finance--${emphasis}` : ""}><dt>{label}</dt><dd>{money(value)}</dd></div>)}</dl>
          <p className="production-details__source">Source du coût : <strong>{summary.has_cost_snapshot == null ? "—" : summary.has_cost_snapshot ? "Coût enregistré" : "Estimation"}</strong></p>
        </aside>
      </div>
    </>}
  </Modal>;
}
